export interface SectionStatus {
  isValid: boolean;
  isCompleted: boolean;
  isRequired: boolean;
  completedAt: string | null;
  completedBy: string | null;
  lastValidatedAt: string | null;
  validationErrors: string[];
  validationMessage: string;
  metadata?: {
    uuid?: string;
    hasMultipleClients?: boolean;
  };
}

export interface BusinessSettings {
  businessDetails: SectionStatus;
  websiteDetails: SectionStatus;
  emailNotificationSettings: SectionStatus;
  accessControl: SectionStatus;
  displayOptions: SectionStatus;
  isCompleted: boolean;
}

export interface GettingStarted {
  stripe: SectionStatus;
  generalWalkthrough: SectionStatus;
  /**
   * Solo visit markers, recorded by the Driver App when the operator opens the corresponding screen.
   * Read `isCompleted`; the API forces `isValid` true and excludes these from `isCompleted` below
   * and from `overallCompletion`, so they cannot affect Fleet or Enterprise progress.
   *
   * Undefined against an API older than web-app `4f377f455`.
   */
  soloPricingVisited?: SectionStatus;
  soloNetworkVisited?: SectionStatus;
  isCompleted: boolean;
}

export interface TripSettings {
  tripCost: SectionStatus;
  timeDayMultiplier: SectionStatus;
  hourlyHire: SectionStatus;
  transferAndVehicleOptions: SectionStatus;
  unscheduledStops: SectionStatus;
  cancellationFees: SectionStatus;
  billingSettings: SectionStatus;
  waitingTimes: SectionStatus;
  dispatchSettings: SectionStatus;
  isCompleted: boolean;
}

export interface Simulation {
  createClient: SectionStatus;
  createDriver: SectionStatus;
  createVehicle: SectionStatus;
  createBooking: SectionStatus;
  isCompleted: boolean;
}

export interface FleetSetup {
  addOwnVehicles: SectionStatus;
  inviteOwnPartnerDrivers: SectionStatus;
  isCompleted: boolean;
}

export interface OnboardingClients {
  registerClient: SectionStatus;
  uploadClients: SectionStatus;
  setupCompanyProfile: SectionStatus;
  isCompleted: boolean;
}

export interface AddOns {
  setupAddons: SectionStatus;
  isCompleted: boolean;
}

export interface OverallCompletion {
  completedSections: number;
  totalSections: number;
  percentage: number;
  validSections: number;
  validPercentage: number;
  isOnboardingComplete: boolean;
}

export interface OnboardingStatus {
  gettingStarted: GettingStarted;
  businessSettings: BusinessSettings;
  tripSettings: TripSettings;
  simulation: Simulation;
  fleetSetup: FleetSetup;
  onboardingClients: OnboardingClients;
  addOns: AddOns;
  overallCompletion: OverallCompletion;
}

export enum BusinessSettingsSection {
  BUSINESS_DETAILS = "business_details",
  WEBSITE_DETAILS = "website_details",
  EMAIL_NOTIFICATION_SETTINGS = "email_notification_settings",
  ACCESS_CONTROL = "access_control",
  DISPLAY_OPTIONS = "display_options",
}

export enum GettingStartedSection {
  STRIPE = "stripe",
  GENERAL_WALKTHROUGH = "general_walkthrough",
}

export enum TripSettingsSection {
  TRIP_COST = "trip_cost",
  TIME_DAY_MULTIPLIER = "time_day_multiplier",
  HOURLY_HIRE = "hourly_hire",
  TRANSFER_AND_VEHICLE_OPTIONS = "transfer_and_vehicle_options",
  UNSCHEDULED_STOPS = "unscheduled_stops",
  CANCELLATION_FEES = "cancellation_fees",
  BILLING_SETTINGS = "billing_settings",
  WAITING_TIMES = "waiting_times",
  DISPATCH_SETTINGS = "dispatch_settings",
}

export enum SimulationSection {
  CREATE_CLIENT = "create_client",
  CREATE_DRIVER = "create_driver",
  CREATE_VEHICLE = "create_vehicle",
  CREATE_BOOKING = "create_booking",
}

export enum FleetSetupSection {
  ADD_OWN_VEHICLES = "add_own_vehicles",
  /** @deprecated Removed from the Quick Setup flow; backend no longer tracks this section. Retained for type compatibility. */
  ADD_OWN_DRIVERS = "add_own_drivers",
  INVITE_OWN_PARTNER_DRIVERS = "invite_own_partner_drivers",
}

export enum OnboardingClientsSection {
  REGISTER_CLIENT = "register_client",
  UPLOAD_CLIENTS = "upload_clients",
  SETUP_COMPANY_PROFILE = "setup_company_profile",
}

export enum AddOnsSection {
  SETUP_ADDONS = "setup_addons",
}

export type OnboardingSection =
  | BusinessSettingsSection
  | GettingStartedSection
  | TripSettingsSection
  | SimulationSection
  | FleetSetupSection
  | OnboardingClientsSection
  | AddOnsSection;
